import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	fetchVehicleBrands,
	fetchVehicleModels,
	fetchVehicleTypes,
} from "@services/vehicleSpecService";
import { getFromStorage, saveToStorage } from "@utils/storage";
import { VehicleData } from "store/slices/vehicleSpecSlice";

const VEHICLE_STORAGE_KEY = "vehicle_data";
const CACHE_DURATION_DAYS = 3; // 3 days

const isVehicleDataCacheValid = (data: VehicleData | null): data is VehicleData => {
	if (!data) return false;

	if (
		!Array.isArray(data.brands) ||
		!Array.isArray(data.types) ||
		!Array.isArray(data.models)
	) {
		return false;
	}

	return data.types.every(
		(type) =>
			type &&
			"title_ru" in type &&
			"title_en" in type &&
			"title_tk" in type,
	);
};

/**
 * Thunk to fetch vehicle data (only if needed)
 */
export const fetchVehicleDataThunk = createAsyncThunk<VehicleData>(
	"vehicles/fetchAll",
	async (_, { rejectWithValue }) => {
		try {
			// Check for cached data in localStorage
			const cachedData = getFromStorage<VehicleData>(VEHICLE_STORAGE_KEY);
			if (isVehicleDataCacheValid(cachedData)) return cachedData;

			// Fetch new data from API
			const [brands, types, models] = await Promise.all([
				fetchVehicleBrands(),
				fetchVehicleTypes(),
				fetchVehicleModels(),
			]);

			// Extract only `data` fields
			const vehicleData: VehicleData = {
				brands: brands.data.data,
				types: types.data.data,
				models: models.data.data,
			};

			// Store in localStorage with expiration
			saveToStorage(
				VEHICLE_STORAGE_KEY,
				vehicleData,
				false,
				CACHE_DURATION_DAYS,
			);

			return vehicleData;
		} catch (error) {
			console.error("Error fetching vehicle data:", error);
			return rejectWithValue("Failed to fetch vehicle data.");
		}
	},
);
